import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import type { ReportDefinition } from "../lib/reports.js";

/**
 * Per-report "What this report shows" / "How ... are calculated" guide content, shown in
 * the `ReportGuideAccordion` behind each report's `ReportGuideToggleButton`.
 *
 * PORTED FROM `ecom_code` VERBATIM WHERE THE UNDERLYING REPORT IS THE SAME (owner request,
 * 2026-09-15: "whatever info we're showing in ecom_code we can show that"). Source
 * Attribution's columns match legacy's exactly, so its copy is a straight port. Every
 * other report here has ALREADY DIVERGED from legacy in some real way — different column
 * set, a reconciliation row legacy doesn't have, a model picker with different objective
 * names — so its copy is ADAPTED to what this platform actually shows rather than
 * blindly re-pasting legacy's text over a different report. Each spot where that
 * happened is called out below; if the underlying report changes again, this needs a
 * matching update (and remember to say so, per the same owner request).
 */

export interface ReportGuideContent {
  readonly whatContent: React.ReactNode;
  readonly howTitle?: string;
  readonly howBadge?: string;
  readonly howContent: React.ReactNode;
}

/** Source Attribution — a straight port. `ecom_code`'s columns and models are identical. */
function sourceAttributionGuide(): ReportGuideContent {
  return {
    howTitle: "How attribution models & metrics are calculated",
    howBadge: "Attribution Logic",
    whatContent: (
      <div className={noteText}>
        <p className="m-0">
          Source Attribution gives you an unbiased breakdown of your marketing performance across all traffic sources, ad
          channels, and organic touchpoints.
        </p>
        <ul className="m-0 mt-2 list-disc space-y-1.5 pl-[18px]">
          <li>
            <strong>Spend:</strong> Ad platform spend reported directly from synced advertising accounts.
          </li>
          <li>
            <strong>Platform ROAS:</strong> ROAS as reported directly inside the ad platform (Meta / Google Ads).
          </li>
          <li>
            <strong>FC (First-Click):</strong> 100% of conversion credit is assigned to the channel that introduced the
            customer on their very first session.
          </li>
          <li>
            <strong>LC (Last-Click):</strong> 100% of conversion credit is assigned to the channel that immediately
            preceded the checkout.
          </li>
          <li>
            <strong>DDA (Data-Driven Attribution):</strong> Multi-touch machine-learning algorithm distributing fractional
            credit based on touchpoint conversion influence.
          </li>
          <li>
            <strong>Triple Attribution:</strong> Gives full credit (1.0 order and 100% revenue) to every channel touched
            in the customer journey.
          </li>
          <li>
            <strong>Linear Attribution:</strong> Divides conversion credit equally across all recorded sessions in the
            customer journey.
          </li>
        </ul>
      </div>
    ),
    howContent: (
      <div className={noteText}>
        <p className="m-0">How attribution models &amp; metrics are calculated:</p>
        <FormulaList>
          <li>
            <strong>Spend:</strong> directly synced ad platform spend for the selected period.
          </li>
          <li>
            <strong>Platform ROAS:</strong> Platform Reported Revenue ÷ Platform Spend.
          </li>
          <li>
            <strong>ROAS (LC):</strong> Last-Click Attributed Shopify Revenue ÷ Channel Spend.
          </li>
          <li>
            <strong>ROAS (FC):</strong> First-Click Attributed Shopify Revenue ÷ Channel Spend.
          </li>
          <li>
            <strong>ROAS (DDA):</strong> Data-Driven Attributed Revenue ÷ Channel Spend.
          </li>
          <li>
            <strong>ROAS (Triple):</strong> Triple Attributed Revenue ÷ Channel Spend.
          </li>
          <li>
            <strong>ROAS (Linear):</strong> Linear Attributed Revenue ÷ Channel Spend.
          </li>
          <li>
            <strong>CPS:</strong> total Spend ÷ total EA Sessions.
          </li>
        </FormulaList>
      </div>
    ),
  };
}

/**
 * Meta / Google Campaign Attribution.
 *
 * ADAPTED, not a straight port: `ecom_code`'s equivalent guide also lists CPA, AOV, and a
 * Last-Click-vs-Assisted "Conversion Credit" group — none of which exist as columns on
 * THIS platform's campaign report (`CAMPAIGN_COLUMNS` in `lib/reports.ts` has no
 * cpa/aov/assisted fields; there is no assisted-attribution concept here at all, that
 * lives in a separate report). Kept legacy's intro/structure and every formula that DOES
 * match a real column (ROAS/FC/LC/DDA/Linear/Triple, all present); dropped the three that
 * don't, and added the actual attribution-objective picker this report has instead
 * (`CAMPAIGN_ATTRIBUTION_MODEL_TOGGLE`), which legacy's guide text does not mention.
 */
function campaignAttributionGuide(platformLabel: "Meta" | "Google"): ReportGuideContent {
  return {
    howTitle:
      platformLabel === "Meta"
        ? "How campaign attribution metrics are calculated"
        : "How Google campaign metrics are calculated",
    howBadge: `${platformLabel} Attribution Logic`,
    whatContent: (
      <div className={noteText}>
        <p className="m-0">
          {platformLabel} Campaign Attribution breaks down your {platformLabel} advertising performance across the full
          campaign hierarchy:
        </p>
        <HighlightGrid>
          <HighlightCard tone="blue" title="1. Campaign / Adset / Ad">
            Use the "Level" dropdown, or click a row, to drill from the campaign down into individual{" "}
            {platformLabel === "Meta" ? "adsets or ads" : "ad groups, ads, or asset groups"}.
          </HighlightCard>
          <HighlightCard tone="purple" title="2. Spend & Return Metrics">
            Spend, Platform ROAS, and ROAS/Purchases/Revenue for each attribution model show how efficiently each entity
            converted ad spend into revenue.
          </HighlightCard>
          <HighlightCard tone="emerald" title="3. Attribution Objective">
            "What is your objective?" pins the columns to one lens — Optimize the account (Triple + DDA), Analyse
            awareness (First-click + DDA), or Analyse conversion (Last-click + DDA) — or pick models freely.
          </HighlightCard>
        </HighlightGrid>
      </div>
    ),
    howContent: (
      <div className={noteText}>
        <p className="m-0">How {platformLabel} campaign metrics are calculated:</p>
        <FormulaList>
          <li>
            <strong>ROAS:</strong> Platform Revenue ÷ Spend.
          </li>
          <li>
            <strong>ROAS (FC):</strong> First-Click Revenue ÷ Spend.
          </li>
          <li>
            <strong>ROAS (LC):</strong> Last-Click Revenue ÷ Spend.
          </li>
          <li>
            <strong>ROAS (DDA):</strong> Data-Driven Revenue ÷ Spend.
          </li>
          <li>
            <strong>ROAS (Linear):</strong> Linear Revenue ÷ Spend.
          </li>
          <li>
            <strong>Purchases (Platform):</strong> the {platformLabel === "Meta" ? "Meta" : "Google"}-reported conversion
            count — not derived from our own order matching, so unlike every other model's Purchases cell it is not
            clickable through to an order list.
          </li>
          <li>
            <strong>Purchases (FC/LC/DDA/Linear):</strong> this store's own Shopify orders matched to the entity under
            that model — click a cell to see the order list.
          </li>
        </FormulaList>
      </div>
    ),
  };
}

/**
 * Landing Page Analysis. `ecom_code`'s "Traffic" / "New Visitor %" / "Orders & CVR%"
 * structure and both formulas transfer over exactly — this platform's `LANDING_PAGE_COLUMNS`
 * has the same Sessions/Pageviews/Visitors/New Visitors/Orders/CVR% shape.
 */
function landingPageGuide(): ReportGuideContent {
  return {
    howTitle: "How landing page metrics are calculated",
    whatContent: (
      <div className={noteText}>
        <p className="m-0">
          Landing Page Analysis ranks every URL visitors first land on by traffic volume and how well that traffic
          converts:
        </p>
        <HighlightGrid>
          <HighlightCard tone="blue" title="Traffic">
            Sessions, Page views, Visitors, and New visitors show how much traffic each landing page pulled in.
          </HighlightCard>
          <HighlightCard tone="purple" title="New visitor %">
            How much of a page's traffic is first-time visitors vs. returning ones.
          </HighlightCard>
          <HighlightCard tone="emerald" title="Orders & CVR %">
            Orders and conversion rate (CVR %) show which landing pages actually drive revenue, not just visits.
          </HighlightCard>
        </HighlightGrid>
      </div>
    ),
    howContent: (
      <div className={noteText}>
        <p className="m-0">How each column is calculated:</p>
        <FormulaList>
          <li>
            <strong>New visitor %:</strong> (New visitors ÷ Visitors) × 100.
          </li>
          <li>
            <strong>CVR %:</strong> (Orders ÷ Sessions) × 100.
          </li>
          <li>
            <strong>Non-ecom traffic:</strong> a raw count of sessions this page attracted that never reached a
            product/cart/checkout page — not a percentage.
          </li>
        </FormulaList>
      </div>
    ),
  };
}

/**
 * Landing Page Analysis (DOW). ADAPTED: `ecom_code`'s equivalent guide also promises
 * Customer %, New Customer %, and Non-Ecom Visitor % columns that this report's actual
 * columns (`withoutSort(LANDING_PAGE_COLUMNS)` plus `week_num` — the same set as Landing
 * Page Analysis, no separate customer columns) do not have. Kept the two formulas that do
 * match a real column here.
 */
function landingPageTrendGuide(): ReportGuideContent {
  return {
    howTitle: "How weekly funnel metrics are calculated",
    whatContent: (
      <div className={noteText}>
        <p className="m-0">
          The same landing pages as Landing Page Analysis, broken down <strong>week by week</strong> from the start of
          your date range — not calendar weeks — so trends and drop-offs are easy to spot.
        </p>
        <HighlightGrid cols={2}>
          <HighlightCard tone="blue" title="1. Expandable weekly rows">
            Click the ▶ arrow on any landing page row to expand its week-by-week breakdown.
          </HighlightCard>
          <HighlightCard tone="purple" title="2. Same metrics, per week">
            Sessions, Page views, Visitors, New visitors, Orders, and CVR % — the same columns as Landing Page Analysis,
            split into weekly slices.
          </HighlightCard>
        </HighlightGrid>
      </div>
    ),
    howContent: (
      <div className={noteText}>
        <p className="m-0">How each column is calculated:</p>
        <FormulaList>
          <li>
            <strong>New visitor %:</strong> (New visitors ÷ Visitors) × 100.
          </li>
          <li>
            <strong>CVR %:</strong> (Orders ÷ Sessions) × 100.
          </li>
          <li>A session visiting three pages appears once per page, so these columns do not add up to your session count.</li>
        </FormulaList>
      </div>
    ),
  };
}

export function reportGuideContent(report: ReportDefinition): ReportGuideContent {
  switch (report.key) {
    case "source-attribution":
      return sourceAttributionGuide();
    case "meta-campaign-attribution":
      return campaignAttributionGuide("Meta");
    case "google-campaign-attribution":
      return campaignAttributionGuide("Google");
    case "landing-pages":
      return landingPageGuide();
    case "landing-page-trend":
      return landingPageTrendGuide();
    default:
      // No report currently falls here — every key in `REPORTS` is handled above. Falling
      // back to the report's own subtitle/scopeNote keeps a newly-added report usable
      // (rather than crashing) until its guide content is written.
      return {
        whatContent: <p className="m-0 text-[13px] leading-relaxed">{report.subtitle}</p>,
        howContent: <p className="m-0 text-[13px] leading-relaxed">{report.scopeNote}</p>,
      };
  }
}
